import React, { useState, useEffect } from 'react';
import OrderModalProductsList from '../orders/OrderModalProductsList';
import { FormGroup, Label, ValueField } from '../SharedComponents';
import { CircularProgress, FormControl, InputLabel, Select, MenuItem, Button, Chip } from '@mui/material';
import apiClient from '../../services/apiClient';
import notify from '../../services/notify';
import ReactGA from 'react-ga4';

interface Props {
	order: any;
	isOrderVariantsLoading: boolean;
	variants: any;
	totalOrderPrice: number | null;
	onOrderUpdate?: (updatedOrder: any) => void;
}

const OrderDetails = ({ order, isOrderVariantsLoading, variants, totalOrderPrice, onOrderUpdate }: Props) => {
	const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

	const formatScheduledDate = (dateString: string): string => {
		const date = new Date(dateString);

		// Get ordinal suffix for day
		const getOrdinalSuffix = (day: number): string => {
			if (day > 3 && day < 21) return 'th';
			switch (day % 10) {
				case 1:
					return 'st';
				case 2:
					return 'nd';
				case 3:
					return 'rd';
				default:
					return 'th';
			}
		};

		const day = date.getDate();
		const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
		const month = date.toLocaleDateString('en-US', { month: 'short' });
		const year = date.getFullYear();
		const time = date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});

		return `${dayWithSuffix} ${month} ${year}, ${time}`;
	};

	const statusOptions = [
		{ value: 'pending_approval', label: 'Pending Approval', color: '#f59e0b' },
		{ value: 'accepted', label: 'Accepted', color: '#10b981' },
		{ value: 'rejected', label: 'Rejected', color: '#ef4444' },
		{ value: 'processing', label: 'Processing', color: '#3b82f6' },
		{ value: 'dispatched', label: 'Dispatched', color: '#6366f1' },
		{ value: 'completed', label: 'Completed', color: '#10b981' },
	];

	const handleStatusChange = async (newStatus: string) => {
		if (!order?.id || newStatus === order?.status) return;

		setIsUpdatingStatus(true);
		try {
			const response = await apiClient.orders.updateOrderStatus(order.id, newStatus);
			if (response.status === 'Success') {
				notify('success', response.message || 'Order status updated successfully');

				// Update the order object and notify parent
				const updatedOrder = { ...order, status: newStatus };
				if (onOrderUpdate) {
					onOrderUpdate(updatedOrder);
				}

				ReactGA.event({
					category: 'Order',
					action: 'Updated Order Status',
					label: `Order ID: ${order.id}, Status: ${newStatus}`,
				});
			}
		} catch (error) {
			console.error('Error updating order status:', error);
			notify('error', 'Failed to update order status');

			ReactGA.event({
				category: 'Order',
				action: 'Failed to Update Order Status',
				label: `Order ID: ${order.id}`,
			});
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	return (
		<>
			{/* <ModalTitle>Order Details</ModalTitle> */}
			<div style={{ display: 'flex', flexDirection: 'row' }}>
				<FormGroup style={{ flex: 1 }}>
					<Label>Ordered By</Label>
					<ValueField>{order?.customer_name}</ValueField>
				</FormGroup>
				{order?.order_address && (
					<FormGroup style={{ flex: 1 }}>
						<Label>Delivery Address</Label>
						<ValueField>{order?.order_address}</ValueField>
					</FormGroup>
				)}
			</div>
			<div style={{ display: 'flex', flexDirection: 'row' }}>
				{order?.order_contact && (
					<FormGroup style={{ flex: 1 }}>
						<Label>Phone</Label>
						<ValueField>{order?.order_contact}</ValueField>
					</FormGroup>
				)}
				{order?.customer_email && (
					<FormGroup style={{ flex: 1 }}>
						<Label>Email</Label>
						<ValueField>{order?.customer_email}</ValueField>
					</FormGroup>
				)}
			</div>

			{/* Scheduled Delivery Date */}
			{order?.scheduled_date && (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<FormGroup style={{ flex: 1 }}>
						<Label>Scheduled for</Label>
						<ValueField>{formatScheduledDate(order.scheduled_date)}</ValueField>
					</FormGroup>
				</div>
			)}

			{/* Order Status Section */}
			<div style={{ display: 'flex', flexDirection: 'row', marginTop: '20px' }}>
				<FormGroup style={{ flex: 1 }}>
					<Label>Order Status</Label>
					<FormControl fullWidth size="small" disabled={isUpdatingStatus}>
						<Select
							value={order?.status || ''}
							onChange={(e) => handleStatusChange(e.target.value)}
							displayEmpty
							sx={{ minWidth: 200 }}
							renderValue={(selected) => {
								const selectedOption = statusOptions.find((option) => option.value === selected);
								if (!selectedOption) return '';
								return (
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<div
											style={{
												width: '12px',
												height: '12px',
												borderRadius: '50%',
												backgroundColor: selectedOption.color,
												flexShrink: 0,
											}}
										/>
										<span>{selectedOption.label}</span>
									</div>
								);
							}}
						>
							{statusOptions.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
										<div
											style={{
												width: '12px',
												height: '12px',
												borderRadius: '50%',
												backgroundColor: option.color,
												flexShrink: 0,
											}}
										/>
										<span>{option.label}</span>
									</div>
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{isUpdatingStatus && (
						<div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', color: '#666' }}>
							<CircularProgress size={16} style={{ marginRight: '8px' }} />
							<span style={{ fontSize: '14px' }}>Updating status...</span>
						</div>
					)}
				</FormGroup>
			</div>

			<hr style={{ margin: '50px 0px 30px 0px', backgroundColor: '#f3f3f3', border: 'none', height: '1px' }} />

			<div>
				{isOrderVariantsLoading ? (
					<div
						style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', padding: '50px 0px' }}
					>
						<CircularProgress size={30} color="inherit" />
					</div>
				) : (
					<OrderModalProductsList totalOrderPrice={totalOrderPrice} orderVariants={variants} />
				)}
			</div>
		</>
	);
};

export default OrderDetails;
